import type { ColumnDto } from '@kanban/shared';
import { useState } from 'react';
import { useArchiveColumn, usePatchColumn } from '../lib/boards.js';

/**
 * Spalten-Einstellungen (SPEC 9.2). Die API konnte das von Anfang an, nur das
 * UI fehlte — an eine falsch benannte Spalte kam man bisher gar nicht heran.
 */
export function ColumnSettings({
  boardId,
  column,
  onClose,
}: {
  boardId: string;
  column: ColumnDto;
  onClose: () => void;
}) {
  const patch = usePatchColumn(boardId);
  const archive = useArchiveColumn(boardId);

  const [title, setTitle] = useState(column.title);
  const [wipLimit, setWipLimit] = useState(column.wipLimit === null ? '' : String(column.wipLimit));
  const [isDoneColumn, setIsDoneColumn] = useState(column.isDoneColumn);

  function save() {
    const limit = wipLimit.trim() === '' ? null : Number(wipLimit);
    patch.mutate(
      {
        columnId: column.id,
        patch: {
          ...(title.trim() && title !== column.title ? { title: title.trim() } : {}),
          ...(limit !== column.wipLimit ? { wipLimit: limit } : {}),
          ...(isDoneColumn !== column.isDoneColumn ? { isDoneColumn } : {}),
        },
        updatedAt: column.updatedAt,
      },
      { onSuccess: onClose },
    );
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-end justify-center bg-[rgb(0_0_0/45%)] md:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Spalte ${column.title}`}
      data-testid="column-settings"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="w-full rounded-t-[var(--radius-lg)] bg-surface p-4 shadow-[var(--shadow-3)] md:max-w-sm md:rounded-[var(--radius-lg)]">
        <h2 className="text-sm font-semibold">Spalte</h2>

        <label className="mt-3 block text-xs font-medium text-muted" htmlFor="column-title">
          Titel
        </label>
        <input
          id="column-title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2"
          style={{ minHeight: 'var(--touch-target)' }}
        />

        <label className="mt-3 block text-xs font-medium text-muted" htmlFor="column-wip">
          WIP-Limit (leer = keines)
        </label>
        <input
          id="column-wip"
          type="number"
          min={1}
          max={999}
          inputMode="numeric"
          value={wipLimit}
          onChange={(event) => setWipLimit(event.target.value)}
          className="mt-1 w-full rounded-[var(--radius)] border border-border bg-surface px-3 py-2"
          style={{ minHeight: 'var(--touch-target)' }}
        />
        <p className="mt-1 text-xs text-muted">
          Bei Überschreitung erscheint ein Hinweis — verschieben bleibt möglich.
        </p>

        <label className="mt-3 flex items-center gap-2 text-sm" htmlFor="column-done">
          <input
            id="column-done"
            type="checkbox"
            checked={isDoneColumn}
            onChange={(event) => setIsDoneColumn(event.target.checked)}
            className="h-5 w-5 accent-[var(--color-accent)]"
          />
          Erledigt-Spalte
        </label>
        <p className="mt-1 text-xs text-muted">
          Karten, die hierhin verschoben werden, gelten als erledigt.
        </p>

        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={save}
            disabled={patch.isPending}
            className="flex-1 rounded-[var(--radius)] bg-accent px-4 text-sm font-medium text-[var(--color-accent-contrast)] disabled:opacity-60"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            Speichern
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[var(--radius)] border border-border px-4 text-sm"
            style={{ minHeight: 'var(--touch-target)' }}
          >
            Schließen
          </button>
        </div>

        {/*
          Ohne Rueckfrage, dafuer mit Ruecknahme: der Toast bietet
          „Rückgängig" an und holt Spalte samt ihren Karten zurueck. Ein
          `window.confirm` waere auf dem Smartphone ein Systemdialog mitten im
          Sheet — und beantwortet wird er ohnehin reflexhaft mit OK.
        */}
        <button
          type="button"
          data-testid="remove-column"
          onClick={() => {
            archive.mutate(column.id);
            onClose();
          }}
          className="mt-3 w-full rounded-[var(--radius)] border border-danger px-4 text-sm text-danger"
          style={{ minHeight: 'var(--touch-target)' }}
        >
          Spalte entfernen
        </button>
        <p className="mt-1 text-center text-xs text-muted">
          Karten wandern mit ins Archiv und lassen sich von dort zurückholen.
        </p>
      </div>
    </div>
  );
}
